import {convertISOToDate} from "@/utils";

// Libellés des valeurs gérées par ms-purchase (voir les enums du microservice)

export const PURCHASE_STATUS_ENUM = {
    TO_REVIEW: {label: "À vérifier", value: "TO_REVIEW"},
    TO_PAY: {label: "À payer", value: "TO_PAY"},
    PARTIALLY_PAID: {label: "Partiellement payé", value: "PARTIALLY_PAID"},
    PAID: {label: "Payé", value: "PAID"},
};

// Un avoir fournisseur ne se paie pas : il est remboursé par le fournisseur ou compensé sur la facture qu'il vise
export const PURCHASE_CREDIT_NOTE_STATUS_ENUM = {
    TO_REVIEW: {label: "À vérifier", value: "TO_REVIEW"},
    TO_PAY: {label: "À rembourser", value: "TO_PAY"},
    PARTIALLY_PAID: {label: "Remboursé en partie", value: "PARTIALLY_PAID"},
    PAID: {label: "Soldé", value: "PAID"},
};

export const PURCHASE_CATEGORY_ENUM = {
    GOODS: {label: "Marchandises", value: "GOODS"},
    RAW_MATERIALS: {label: "Matières premières", value: "RAW_MATERIALS"},
    SUBCONTRACTING: {label: "Sous-traitance", value: "SUBCONTRACTING"},
    SERVICES: {label: "Prestations de services", value: "SERVICES"},
    EQUIPMENT: {label: "Matériel et outillage", value: "EQUIPMENT"},
    TRANSPORT: {label: "Transport", value: "TRANSPORT"},
    RENT: {label: "Loyers et charges", value: "RENT"},
    UTILITIES: {label: "Énergie, eau, télécoms", value: "UTILITIES"},
    INSURANCE: {label: "Assurances", value: "INSURANCE"},
    SOFTWARE: {label: "Logiciels et abonnements", value: "SOFTWARE"},
    FEES: {label: "Honoraires et frais", value: "FEES"},
    OTHER: {label: "Autre", value: "OTHER"},
};

// Facture ou avoir fournisseur : les montants d'un avoir sont enregistrés positifs, et se retranchent des totaux
export const PURCHASE_DOCUMENT_TYPE_ENUM = {
    INVOICE: {label: "Facture", value: "INVOICE"},
    CREDIT_NOTE: {label: "Avoir", value: "CREDIT_NOTE"},
};

// Avancement de la livraison d'un achat de biens
export const DELIVERY_STATUS_ENUM = {
    NOT_RECEIVED: {label: "À recevoir", value: "NOT_RECEIVED"},
    PARTIALLY_RECEIVED: {label: "Reçu en partie", value: "PARTIALLY_RECEIVED"},
    RECEIVED: {label: "Reçu", value: "RECEIVED"},
};

// Clé de répartition d'une part de ventilation
export const ALLOCATION_METHOD_ENUM = {
    AMOUNT: {label: "€ HT", value: "AMOUNT"},
    PERCENT: {label: "%", value: "PERCENT"},
    QUANTITY: {label: "Quantité", value: "QUANTITY"},
};

// Catégories dont les biens se réceptionnent, et celles dont les biens vont en stock (voir purchaseCategoryEnum du microservice)
export const RECEIVABLE_CATEGORIES = ["GOODS", "RAW_MATERIALS", "EQUIPMENT"];

// Unités proposées pour une ligne d'article (une autre unité reste possible à la reprise d'une facture reçue)
export const PURCHASE_UNITS = ["u", "m", "m²", "m³", "km", "kg", "t", "L", "h", "j", "forfait", "lot"];

export const PURCHASE_SOURCE_ENUM = {
    MANUAL: {label: "Saisie manuelle", value: "MANUAL"},
    INCOMING_INVOICE: {label: "Facture reçue", value: "INCOMING_INVOICE"},
};

export const DISBURSEMENT_PAYMENT_TYPE_ENUM = {
    TRANSFER: {label: "Virement", value: "TRANSFER"},
    CARD: {label: "Carte bancaire", value: "CARD"},
    DIRECT_DEBIT: {label: "Prélèvement", value: "DIRECT_DEBIT"},
    CHECK: {label: "Chèque", value: "CHECK"},
    CASH: {label: "Espèces", value: "CASH"},
};

// Taux de TVA proposés pour une ligne d'achat (un autre taux reste possible à la reprise d'une facture reçue)
export const VAT_RATES = [20, 10, 5.5, 2.1, 0];

// Erreur d'arrondi des flottants : tout est calculé en centimes
export function round2(value) {
    return Math.round((Number(value) || 0) * 100) / 100;
}

export function formatAmount(amount) {
    return (Number(amount) || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
}

// Les quantités s'arrondissent au millième
export function roundQuantity(value) {
    return Math.round((Number(value) || 0) * 1000) / 1000;
}

// 1250 m -> « 1 250 m »
export function formatQuantity(quantity, unit) {
    const text = (Number(quantity) || 0).toLocaleString('fr-FR', {maximumFractionDigits: 3});
    return unit ? `${text} ${unit}` : text;
}

export function isCreditNote(purchase) {
    return purchase?.documentType === PURCHASE_DOCUMENT_TYPE_ENUM.CREDIT_NOTE.value;
}

// Statuts à afficher pour un achat : ceux d'une facture ou ceux d'un avoir
export function statusEnumOf(purchase) {
    return isCreditNote(purchase) ? PURCHASE_CREDIT_NOTE_STATUS_ENUM : PURCHASE_STATUS_ENUM;
}

// +1 pour une facture, -1 pour un avoir : à appliquer partout où l'on additionne des achats
export function documentSign(purchase) {
    return isCreditNote(purchase) ? -1 : 1;
}

// Montant d'un achat avec son signe (un avoir s'affiche en négatif)
export function formatSignedAmount(purchase, amount) {
    return formatAmount(documentSign(purchase) * (Number(amount) || 0));
}

// Identifiant de 24 caractères hexadécimaux, comme ceux que le serveur donne aux lignes d'achat
export function newLineId() {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Unités proposées, avec celle de la ligne même si elle n'est pas standard
export function unitSelectValues(unit) {
    const units = unit && !PURCHASE_UNITS.includes(unit) ? [...PURCHASE_UNITS, unit] : PURCHASE_UNITS;
    return units.map(value => ({label: value, value}));
}

// Taux de TVA : 5.5 -> « 5,5 % »
export function formatRate(rate) {
    return `${String(rate).replace('.', ',')} %`;
}

export function formatDate(date) {
    return date ? new Date(date).toLocaleDateString("fr-FR") : '-';
}

// Valeur d'un champ <input type="date"> (AAAA-MM-JJ), vide si la date est absente
export function toInputDate(date) {
    return date ? convertISOToDate(date) : '';
}

// Les lignes des tableaux portent « id », les documents bruts de la base « _id »
export function idOf(item) {
    return item ? (item.id ?? item._id) : undefined;
}

export function labelOf(enumObject, value, fallback = '-') {
    const found = Object.values(enumObject).find(item => item.value === value);
    return found ? found.label : fallback;
}

export function enumToSelectValues(enumObject) {
    return Object.values(enumObject).map(item => ({label: item.label, value: item.value}));
}

// Les options d'un select de taux de TVA, avec le taux de la ligne même s'il n'est pas standard
export function vatRateSelectValues(rate) {
    const rates = rate !== undefined && rate !== null && !VAT_RATES.includes(Number(rate)) ? [...VAT_RATES, Number(rate)] : VAT_RATES;
    return rates.map(value => ({label: formatRate(value), value}));
}

// Date d'échéance proposée : date de la facture + délai de paiement du fournisseur
export function dueDateFrom(invoiceDate, paymentTermDays) {
    if (!invoiceDate || paymentTermDays === undefined || paymentTermDays === null || paymentTermDays === '') {
        return '';
    }
    const date = new Date(invoiceDate);
    if (isNaN(date.getTime())) {
        return '';
    }
    date.setDate(date.getDate() + Number(paymentTermDays));
    return convertISOToDate(date);
}
